<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import InfoCard from '@@home/components/InfoCard.vue';
import { $fetch } from '@@home/plugins/fetch';
import useMarkdown from '@@home/hooks/markdown';
import { useAsyncData } from '@@home/hooks/async-data';

const { t } = useI18n();
const marked = useMarkdown();

const { data: legalCasesList } = useAsyncData(
  'law:legalCasesList',
  () => $fetch('/law/resource/v1/list', { data: { category: 'gmFDf2', limit: -1 } }),
);

const { data: fileList } = useAsyncData(
  'law:fileList',
  () => $fetch('/law/resource/v1/list', { data: { category: '9Hiqje', limit: -1 } }),
);

const { data: administrativeRegulationFileList } = useAsyncData(
  'law:administrativeRegulationFileList',
  () => $fetch('/law/resource/v1/list', { data: { category: '7x4cxA', limit: -1 } }),
);
</script>

<template>
  <div class="page-law-database">
    <h2 class="m-0 text-center text-32px color-hex-2d2d2d font-medium leading-48px">{{ t('lawData.title1') }}</h2>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div class="markdown mt-32px text-16px color-hex-2d2d2d font-normal leading-24px" v-html="marked.parse(t('lawData.desc'))"></div>

    <div v-if="legalCasesList?.length">
      <ul class="paragraph-ul m-0 p-0">
        <li v-for="item in legalCasesList" :key="item.title" class="m-0 mt-48px list-none p-0">
          <InfoCard :title="item.title">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="markdown description" v-html="marked.parse(item.content)"></div>
          </InfoCard>
        </li>
      </ul>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div class="markdown paragraph-div" v-html="marked.parse(t('lawData.legalExplain'))"></div>
    </div>

    <template v-if="fileList?.length">
      <h2 class="mb-0 mt-48px text-center text-32px color-hex-2d2d2d font-medium leading-48px">{{ t('lawData.title2') }}</h2>
      <ul class="mt-0 flex flex-wrap pl-34px <2xl:pl-16px">
        <li class="card left-top relative mt-[32px] h-[420px] w-[386px] list-none bg-cover bg-no-repeat after:absolute after:left-42px after:top-30px after:h-364px after:w-252px after:content-[''] after:<2xl:h-345px after:<2xl:w-240px <2xl:h-400px <2xl:w-368px" :class="[(inx + 1) % 3 ? 'mr-[82px] <2xl:mr-32px' : '']" v-for="(piece, inx) in fileList" :key="inx">
          <a :href="piece.url" target="_blank" class="absolute left-80px top-187px z-1 hidden h-49px w-176px cursor-pointer border-1 border-white rounded-28px border-solid bg-transparent text-center text-20px text-white font-normal leading-49px no-underline <2xl:left-74px">阅读完整内容</a>
          <span class="ml-[62px] inline-block h-[24px] w-216px pt-[143px] text-center text-[30px] text-white font-medium leading-[45px] <2xl:ml-54px">{{ piece.title }}</span>
        </li>
      </ul>
    </template>

    <template v-if="administrativeRegulationFileList?.length">
      <h2 class="mb-0 mt-48px text-center text-32px color-hex-2d2d2d font-medium leading-48px">{{ t('lawData.title3') }}</h2>
      <ul class="mt-0 flex flex-wrap pl-34px <2xl:pl-16px">
        <li class="card left-top relative mt-[32px] h-[420px] w-[386px] list-none bg-cover bg-no-repeat after:absolute after:left-42px after:top-30px after:h-364px after:w-252px after:content-[''] after:<2xl:h-345px after:<2xl:w-240px <2xl:h-400px <2xl:w-368px" :class="[(inx + 1) % 3 ? 'mr-[82px] <2xl:mr-32px' : '']" v-for="(piece, inx) in administrativeRegulationFileList" :key="inx">
          <a :href="piece.url" target="_blank" class="absolute left-80px top-187px z-1 hidden h-49px w-176px cursor-pointer border-1 border-white rounded-28px border-solid bg-transparent text-center text-20px text-white font-normal leading-49px no-underline <2xl:left-74px">阅读完整内容</a>
          <span class="ml-[62px] inline-block h-[24px] w-216px pt-[143px] text-center text-[30px] text-white font-medium leading-[45px] <2xl:ml-54px">{{ piece.title }}</span>
        </li>
      </ul>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.page-law-database {
  .markdown {
    &:deep() {
      a {
        color: #1180ff;
        text-decoration: none;
        list-style: none;
      }
    }
  }

  .card {
    background-image: url(@@home/assets/law/book-bg.png);

    &:hover {
      &::after {
        background-color: rgba(45, 45, 45, 0.6);
      }

      a {
        display: block;
      }
    }
  }

  .paragraph-ul {
    .description {
      &:deep() {
        p {
          @apply m-0 mt-8px text-16px font-normal leading-24px color-hex-9A9EA6;
        }

        h3 {
          @apply text-18px mb-8px mt-32px font-500 color-hex-2d2d2d first:mt-16px;
        }

        em {
          @apply color-hex-2d2d2d not-italic;
        }
      }
    }
  }

  .paragraph-div {
    &:deep() {
      p {
        @apply m-0 mt-16px text-16px font-normal leading-24px color-hex-2d2d2d;
      }

      li {
        @apply m-0 text-16px font-normal leading-24px color-hex-2d2d2d;
      }
    }
  }
}
</style>
